import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { HERO_IMAGES } from '../data/mock';
import { submitEnquiry } from '../data/api';
import { CONTACT_INFO } from '../components/Footer';
import { toast } from 'sonner';
import Reveal from '../components/Reveal';

const Contact = () => {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const ref = params.get('ref');
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  useEffect(() => {
    if (ref) {
      setForm((f) => ({ ...f, subject: `${t('detail.reference')} ${ref}` }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, property_ref: ref || '', lang });
      toast.success(t('contact.sent'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      // Fallback: keep enquiry locally so nothing is lost
      try {
        const prev = JSON.parse(localStorage.getItem('starkke_enquiries') || '[]');
        prev.push({ ...form, date: new Date().toISOString() });
        localStorage.setItem('starkke_enquiries', JSON.stringify(prev));
      } catch (_) {}
      toast.success(t('contact.sent'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } finally {
      setSubmitting(false);
    }
  };

  const field = 'w-full bg-transparent border-b border-[#1b2331]/25 font-sans-elegant text-[#1b2331] py-3 focus:outline-none focus:border-[#b0894f] transition-colors placeholder:text-[#1b2331]/40';

  return (
    <div className="bg-[#f4efe6]">
      {/* banner */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.interior2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1b2331]/70" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#c8a469] mb-5">STARKKE IMMOBILIER</p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-[#f4efe6] font-light leading-tight">{t('contact.title')}</h1>
          <p className="font-sans-elegant text-[#f4efe6]/70 max-w-xl mt-5 leading-relaxed">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24">
            {/* info */}
            <div className="lg:col-span-4">
              <Reveal>
                <h2 className="font-sans-elegant text-[0.7rem] tracking-brand uppercase text-[#b0894f] mb-8">
                  {t('contact.infoTitle')}
                </h2>
                <ul className="space-y-8">
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 border border-[#1b2331]/20 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-[#b0894f]" />
                    </div>
                    <div>
                      <p className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93] mb-1">Email</p>
                      <a href={`mailto:${CONTACT_INFO.email}`} className="font-serif-display text-xl text-[#1b2331] link-underline">{CONTACT_INFO.email}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 border border-[#1b2331]/20 flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-[#b0894f]" />
                    </div>
                    <div>
                      <p className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93] mb-1">{t('contact.phone')}</p>
                      <a href={`tel:${CONTACT_INFO.tel.replace(/\s/g, '')}`} className="font-serif-display text-xl text-[#1b2331] link-underline">{CONTACT_INFO.tel}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 border border-[#1b2331]/20 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-[#b0894f]" />
                    </div>
                    <div>
                      <p className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93] mb-1">Adresse</p>
                      <p className="font-serif-display text-xl text-[#1b2331] leading-snug">{CONTACT_INFO.address}</p>
                      <p className="font-sans-elegant text-sm text-[#8a8d93] mt-1">{t('contact.hours')}</p>
                    </div>
                  </li>
                </ul>
              </Reveal>
            </div>

            {/* form */}
            <div className="lg:col-span-8">
              <Reveal delay={120}>
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                  <div>
                    <label className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93]">{t('contact.name')}</label>
                    <input name="name" value={form.name} onChange={onChange} required className={field} />
                  </div>
                  <div>
                    <label className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93]">{t('contact.email')}</label>
                    <input type="email" name="email" value={form.email} onChange={onChange} required className={field} />
                  </div>
                  <div>
                    <label className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93]">{t('contact.phone')}</label>
                    <input name="phone" value={form.phone} onChange={onChange} className={field} />
                  </div>
                  <div>
                    <label className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93]">{t('contact.subject')}</label>
                    <input name="subject" value={form.subject} onChange={onChange} className={field} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="font-sans-elegant text-[0.66rem] tracking-wide-2 uppercase text-[#8a8d93]">{t('contact.message')}</label>
                    <textarea name="message" value={form.message} onChange={onChange} rows={5} required className={`${field} resize-none`} />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-3 bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-10 py-4 hover:bg-[#b0894f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? '…' : t('contact.send')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="relative border border-[#1b2331]/10">
            <iframe
              title="STARKKE Immobilier - 10 Boulevard Raspail 75007 Paris"
              src="https://www.google.com/maps?q=10+Boulevard+Raspail+75007+Paris&output=embed"
              width="100%"
              height="460"
              style={{ border: 0, filter: 'grayscale(0.4) contrast(1.05)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
